import { useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import type { Task } from '../store/types';
import { tasksOn } from '../store/planner';
import { MES_CURTO, nomeMes, parseYmd, posNaSemana, semanaNaSeg, today, ymd } from '../lib/date';
import { Sheet } from './ui';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';

const L = ['@letra-seg', '@letra-ter', '@letra-qua', '@letra-qui', '@letra-sex', '@letra-sab', '@letra-dom'];

/** Calendário do mês: pontos nos dias com tarefas, feriados em vermelho. Toque num dia para abrir.
 *  Setas ‹ › mudam o mês, « » mudam o ano; tocar no título abre a escolha rápida de mês e ano. */
export function MonthSheet({ visible, date, tasks, cor, feriado, onPick, onClose }: {
  visible: boolean; date: string; tasks: Task[]; cor: string; feriado: (d: string) => string | null;
  onPick: (d: string) => void; onClose: () => void;
}) {
  const base = parseYmd(date);
  const [mes, setMes] = useState({ y: base.getFullYear(), m: base.getMonth() });
  const [aberto, setAberto] = useState(visible);
  const [escolher, setEscolher] = useState(false); // grade de meses (escolha rápida de mês/ano)
  if (visible !== aberto) { setAberto(visible); if (visible) { setMes({ y: base.getFullYear(), m: base.getMonth() }); setEscolher(false); } }

  const dias = useMemo(() => {
    const ini = new Date(mes.y, mes.m, 1);
    const off = posNaSemana(ini.getDay()); // começa na segunda ou no domingo (Ajustes > Calendário)
    const n = new Date(mes.y, mes.m + 1, 0).getDate();
    return [...Array(off).fill(null), ...Array.from({ length: n }, (_, i) => ymd(new Date(mes.y, mes.m, i + 1)))] as (string | null)[];
  }, [mes]);
  const hoje = today();
  const mudar = (d: number) => setMes(x => { const nd = new Date(x.y, x.m + d, 1); return { y: nd.getFullYear(), m: nd.getMonth() }; });
  const fersDoMes = dias.filter((d): d is string => !!d && !!feriado(d));

  return (
    <Sheet visible={visible} onClose={onClose} title={t('Calendário')}>
      <View style={{ paddingHorizontal: 16, gap: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Pressable onPress={() => mudar(-12)} hitSlop={8} style={{ padding: 8 }} accessibilityLabel={t('Ano anterior')}><Text style={{ color: C.sub, fontSize: 22 }}>«</Text></Pressable>
          <Pressable onPress={() => mudar(escolher ? -12 : -1)} hitSlop={8} style={{ padding: 8 }}><Text style={{ color: C.sub, fontSize: 24 }}>‹</Text></Pressable>
          <Pressable onPress={() => setEscolher(e => !e)} hitSlop={8} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Text style={{ color: C.text, fontSize: 20, fontWeight: '800', textTransform: 'capitalize' }} numberOfLines={1}>{escolher ? mes.y : `${nomeMes(mes.m)} ${mes.y}`}</Text>
            <Text style={{ color: C.sub, fontSize: 12 }}>{escolher ? '▴' : '▾'}</Text>
          </Pressable>
          <Pressable onPress={() => mudar(escolher ? 12 : 1)} hitSlop={8} style={{ padding: 8 }}><Text style={{ color: C.sub, fontSize: 24 }}>›</Text></Pressable>
          <Pressable onPress={() => mudar(12)} hitSlop={8} style={{ padding: 8 }} accessibilityLabel={t('Próximo ano')}><Text style={{ color: C.sub, fontSize: 22 }}>»</Text></Pressable>
        </View>
        {escolher && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
            {MES_CURTO.map((m, i) => {
              const sel = i === mes.m, atual = mes.y === new Date().getFullYear() && i === new Date().getMonth();
              return (
                <Pressable key={m} onPress={() => { setMes(x => ({ y: x.y, m: i })); setEscolher(false); }} style={{ width: '25%', padding: 4 }}>
                  <View style={{ paddingVertical: 16, borderRadius: 12, alignItems: 'center', backgroundColor: sel ? cor : atual ? alpha(cor, 0.18) : C.card }}>
                    <Text style={{ color: sel ? '#fff' : C.text, fontWeight: '700', textTransform: 'capitalize' }}>{t(m)}</Text>
                  </View>
                </Pressable>
              );
            })}
          </View>
        )}
        {!escolher && <>
        <View style={{ flexDirection: 'row' }}>
          {(semanaNaSeg() ? L : [L[6], ...L.slice(0, 6)]).map((l, i) => <Text key={i} style={{ flex: 1, textAlign: 'center', color: (semanaNaSeg() ? i >= 5 : i === 0 || i === 6) ? C.mute : C.sub, fontSize: 12, fontWeight: '700' }}>{t(l)}</Text>)}
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
          {dias.map((d, i) => {
            if (!d) return <View key={'v' + i} style={{ width: '14.2857%', aspectRatio: 0.9 }} />;
            const qtd = tasksOn(d, tasks).length, sel = d === date, fer = !!feriado(d);
            return (
              <Pressable key={d} onPress={() => { onPick(d); onClose(); }} style={{ width: '14.2857%', aspectRatio: 0.9, padding: 3 }}>
                <View style={{ flex: 1, borderRadius: 12, alignItems: 'center', justifyContent: 'center', gap: 3,
                  backgroundColor: sel ? cor : d === hoje ? alpha(cor, 0.18) : C.card }}>
                  <Text style={{ color: sel ? '#fff' : fer ? '#F87171' : C.text, fontWeight: '700', fontSize: 15 }}>{parseYmd(d).getDate()}</Text>
                  <View style={{ flexDirection: 'row', gap: 2, height: 5 }}>
                    {Array.from({ length: Math.min(3, qtd) }).map((_, k) => <View key={k} style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: sel ? '#fff' : cor }} />)}
                  </View>
                </View>
              </Pressable>
            );
          })}
        </View>
        {fersDoMes.length > 0 && (
          <View style={{ gap: 4, backgroundColor: C.card, borderRadius: 14, padding: 12 }}>
            {fersDoMes.map(d => <Text key={d} style={{ color: C.sub, fontSize: 12 }}><Text style={{ color: '#F87171', fontWeight: '700' }}>{parseYmd(d).getDate()}</Text>  {feriado(d)}</Text>)}
          </View>
        )}
        </>}
        <Pressable onPress={() => { onPick(hoje); onClose(); }} style={{ alignSelf: 'center', padding: 10 }}><Text style={{ color: cor, fontWeight: '700' }}>{t('Ir para hoje')}</Text></Pressable>
      </View>
    </Sheet>
  );
}
